import React from 'react'
import { List, useTable, ShowButton, DateField } from '@refinedev/antd'
import { useList } from '@refinedev/core'
import { Table, Space } from 'antd'
import { brand } from '../../theme/brand'
import { useRowShow, ListPage, Pill, TableSurface, type Stat, type Tone } from '../../theme/listkit'
import { Flag, ShieldAlert, ShieldCheck, ScanLine } from '../../components/icons'

interface Report {
  id: string
  reporter_id: string | null
  target_type:
    | 'post'
    | 'photo'
    | 'avatar'
    | 'profile'
    | 'job'
    | 'message'
    | 'business'
    | 'business_vouch'
    | 'business_note'
    | 'business_request'
    | 'business_reply'
    | 'group'
    | 'group_meetup'
    | 'memory_comment'
  target_id: string
  target_user_id: string | null
  reason: 'spam' | 'harassment' | 'hate' | 'nudity' | 'violence' | 'misinfo' | 'other'
  details: string | null
  source: 'member' | 'auto_image'
  status: 'open' | 'reviewing' | 'actioned' | 'dismissed'
  reviewed_by: string | null
  reviewed_at: string | null
  resolution_note: string | null
  created_at: string
}

const reasonTone = (reason: string): Tone => {
  if (['hate', 'harassment', 'violence', 'nudity'].includes(reason)) return 'red'
  if (['spam', 'misinfo'].includes(reason)) return 'gold'
  return 'neutral'
}

const statusTone = (status: string): Tone => {
  if (status === 'open') return 'gold'
  if (status === 'reviewing') return 'jaguar'
  if (status === 'actioned') return 'green'
  return 'neutral'
}

const targetTypeTone = (type: string): Tone => {
  if (type === 'profile') return 'jaguar'
  if (type === 'message') return 'red'
  if (type === 'avatar') return 'jaguar'
  if (type.startsWith('business')) return 'gold'
  if (type === 'group' || type === 'group_meetup') return 'gold'
  return 'neutral'
}

const fmtId = (id: string) => id.slice(0, 8) + '…'

/**
 * Reports queue — the Trust & Safety moderation inbox.
 * useTable drives the table. The StatStrip counts come from four bounded
 * pageSize:1 useLists (one per status); each reads `result.total` so PostgREST
 * returns just the count without ever loading the whole table client-side.
 *
 * Null reporter_id: auto-image reports have reporter_id=null and source='auto_image'.
 * These render as "Auto-moderation" in the reporter column and show an "Auto-image" badge.
 */
export function ReportsList() {
  const { tableProps } = useTable<Report>({
    resource: 'reports',
    syncWithLocation: true,
    sorters: { initial: [{ field: 'created_at', order: 'desc' }] },
  })
  const onRow = useRowShow('reports')

  // Bounded stat counts — one tiny pageSize:1 query per status. PostgREST returns
  // the filtered total without the rows, so the main thread never aggregates the
  // whole table. The four run in parallel.
  const openQ = useList<Report>({
    resource: 'reports',
    filters: [{ field: 'status', operator: 'eq', value: 'open' }],
    pagination: { pageSize: 1 },
  })
  const reviewingQ = useList<Report>({
    resource: 'reports',
    filters: [{ field: 'status', operator: 'eq', value: 'reviewing' }],
    pagination: { pageSize: 1 },
  })
  const actionedQ = useList<Report>({
    resource: 'reports',
    filters: [{ field: 'status', operator: 'eq', value: 'actioned' }],
    pagination: { pageSize: 1 },
  })
  const dismissedQ = useList<Report>({
    resource: 'reports',
    filters: [{ field: 'status', operator: 'eq', value: 'dismissed' }],
    pagination: { pageSize: 1 },
  })

  const open = openQ.result?.total ?? 0
  const reviewing = reviewingQ.result?.total ?? 0
  const actioned = actionedQ.result?.total ?? 0
  const dismissed = dismissedQ.result?.total ?? 0

  const stats: Stat[] = [
    { label: 'Open', value: open.toLocaleString(), icon: Flag, tone: open > 0 ? 'gold' : 'neutral' },
    { label: 'Reviewing', value: reviewing.toLocaleString(), icon: ScanLine, tone: reviewing > 0 ? 'jaguar' : 'neutral' },
    { label: 'Actioned', value: actioned.toLocaleString(), icon: ShieldAlert, tone: actioned > 0 ? 'green' : 'neutral' },
    { label: 'Dismissed', value: dismissed.toLocaleString(), icon: ShieldCheck, tone: 'neutral' },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Trust & Safety · Reports Queue',
        description:
          'Member-submitted and auto-image content reports — flag spam, harassment, hate speech, and policy violations for review. Open reports need triage; actioned ones are resolved with a note.',
      }}
      stats={stats}
      statColumns={4}
    >
      <List title={false} breadcrumb={false}>
        <TableSurface>
          <Table {...tableProps} rowKey="id" scroll={{ x: true }} onRow={onRow}>
            <Table.Column<Report>
              dataIndex="source"
              title="Source"
              render={(source: string) =>
                source === 'auto_image' ? (
                  <Pill tone="jaguar">Auto-image</Pill>
                ) : (
                  <Pill tone="neutral">Member</Pill>
                )
              }
            />
            <Table.Column<Report>
              dataIndex="target_type"
              title="Type"
              render={(type: string) => (
                <Pill tone={targetTypeTone(type)}>{type}</Pill>
              )}
            />
            <Table.Column<Report>
              dataIndex="reason"
              title="Reason"
              render={(reason: string) => (
                <Pill tone={reasonTone(reason)}>{reason}</Pill>
              )}
            />
            <Table.Column<Report>
              dataIndex="reporter_id"
              title="Reporter"
              render={(_: unknown, record: Report) => {
                if (record.source === 'auto_image' || !record.reporter_id) {
                  return (
                    <span
                      style={{
                        fontFamily: brand.fSans,
                        fontWeight: 600,
                        fontSize: 12.5,
                        color: brand.sub,
                        fontStyle: 'italic',
                      }}
                    >
                      Auto-moderation
                    </span>
                  )
                }
                return (
                  <span
                    style={{
                      fontFamily: brand.fSans,
                      fontWeight: 600,
                      fontSize: 12.5,
                      color: brand.sub,
                    }}
                  >
                    {fmtId(record.reporter_id)}
                  </span>
                )
              }}
            />
            <Table.Column<Report>
              dataIndex="status"
              title="Status"
              render={(status: string) => <Pill tone={statusTone(status)}>{status}</Pill>}
            />
            <Table.Column<Report>
              dataIndex="created_at"
              title="Reported"
              sorter
              render={(value: string) => <DateField value={value} format="MMM D, YYYY" />}
            />
            <Table.Column<Report>
              title="Actions"
              dataIndex="actions"
              render={(_: unknown, record: Report) => (
                <Space>
                  <ShowButton hideText size="small" recordItemId={record.id} />
                </Space>
              )}
            />
          </Table>
        </TableSurface>
      </List>
    </ListPage>
  )
}
